import { Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from "@angular/core";
import { BreakpointObserver } from "@angular/cdk/layout";
import { MatDialog } from "@angular/material/dialog";
import { oaDialogConfig } from "app/helpers/oa-dialog";
import { ProposalReceiptsDialogComponent } from "../proposal-receipts-dialog/proposal-receipts-dialog.component";
import { MatSort } from "@angular/material/sort";
import { Router } from "@angular/router";
import { fuseAnimations } from "@fuse/animations";
import { Proposal } from "app/entities/proposal";
import { FilterFieldComponent } from "app/layout/components/table/table.component";
import { TableComponent } from "app/layout/components/table/table.component";
import { ProposalsDatasource } from "app/main/pages/proposals/proposals-datasource";
import { ConfirmActionDialogComponent } from "app/main/components/confirm-action-dialog/confirm-action-dialog.component";
import { ProposalBulkResult, ProposalListSummary, ProposalService } from "app/main/pages/proposals/proposals.service";
import { AuthService } from "app/services/auth.service";
import { EnvService } from "app/services/env.service";
import { AlertService } from "app/services/alert.service";
import { GtagService } from "app/services/gtag.service";
import { SubscriptionRestrictionService } from "app/services/subscription-restriction.service";
import { Observable, Subscription } from "rxjs";
import { UserPreferences, UsersService } from "app/main/pages/users/users.service";
import { formatBRL } from "app/helpers/money";
import {
    activityText,
    bulkResultMessage,
    createdText,
    quickAction,
    QuickActionButton,
    quoteLabel,
    situationView,
    SituationView,
    tagDot,
    tagStyle,
    updatedText,
} from "app/helpers/proposal-list";
import { ProposalActionsComponent, ProposalStatusActionEvent } from "../proposal-actions/proposal-actions.component";

type StatusChipKey = keyof ProposalListSummary['status_counts'];
type Situation = 'awaiting' | 'expiring' | 'approved_month' | 'receivable';

/** Chips de status da lista completa, na ordem da tela */
const STATUS_CHIPS: { key: StatusChipKey; label: string; status: number | null }[] = [
    { key: 'all', label: 'Todos', status: null },
    { key: 'sent', label: 'Enviados', status: 2 },
    { key: 'created', label: 'Criados', status: 1 },
    { key: 'approved', label: 'Aprovados', status: 3 },
    { key: 'billed', label: 'Faturados', status: 5 },
    { key: 'paid', label: 'Pagos', status: 6 },
    { key: 'refused', label: 'Reprovados', status: 4 },
    { key: 'expired', label: 'Expirados', status: 7 },
];

const MONTHS = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'];

@Component({
    selector: "app-proposals-list",
    templateUrl: "./proposals-list.component.html",
    styleUrls: ["./proposals-list.component.scss"],
    animations: fuseAnimations,
})
export class ProposalsListComponent implements OnInit, OnChanges, OnDestroy {
    userPreferences$: Observable<UserPreferences>;

    @ViewChild(MatSort, { static: true }) sort!: MatSort;
    @ViewChild('proposalsList') proposalsTable?: TableComponent<Proposal>;
    @Input("proposalsDataSource") dataSource!: ProposalsDatasource;
    @Input("searchField") searchField?: ElementRef;
    @Input("filterFieldComponent") filterFieldComponent?: FilterFieldComponent;

    @Input("showClient") showClient = true;
    @Input("showCustomer") showCustomer = false;
    @Input("pageSize") pageSize = 10;
    @Input("proposalFilter") proposalFilter = {};
    @Input() bulkActionsEnabled = false;
    @Input() bulkActionMode: "delete" | "restore" = "delete";
    @Input() forcedFilterValues: Record<string, any> = {};
    @Input() showDeletedInfo = false;
    /** ORCA-1965: indicadores no topo e chips de status com contagem (lista completa) */
    @Input() showOverview = false;
    /** ORCA-1968: lista em cards (Início). No celular a lista vira cards sempre (ORCA-1969). */
    @Input() variant: 'table' | 'cards' = 'table';
    /** ORCA-1968: versão compacta da Início (filtros rápidos, sem paginação e "Ver todos") */
    @Input() compact = false;

    @Output("onFilterChange") onFilterChange = new EventEmitter<any>()
    @Output() bulkActionCompleted = new EventEmitter<"delete" | "restore">();

    displayedColumns: string[] = [];

    showTable = true;
    selectedIds = new Set<number>();
    allSelected = false;
    tableParams: any = {};

    readonly statusChips = STATUS_CHIPS;
    readonly approveButton: QuickActionButton = { action: 'aprovar', label: 'Aprovar', icon: 'thumb_up' };
    readonly refuseButton: QuickActionButton = { action: 'reprovar', label: 'Reprovar', icon: 'thumb_down' };
    statusChip: StatusChipKey = 'all';
    situation: Situation | null = null;
    summary: ProposalListSummary | null = null;
    isHandset = false;

    private summarySubscription?: Subscription;
    private subscriptions = new Subscription();

    constructor(
        public _matDialog: MatDialog,
        private _router: Router,
        private _subscriptionRestrictionService: SubscriptionRestrictionService,
        private _gtag: GtagService,
        private _proposalService: ProposalService,
        private _alertService: AlertService,
        private _usersService: UsersService,
        private _breakpointObserver: BreakpointObserver,
        private _authService: AuthService,
        private _envService: EnvService,
    ) {
        this.userPreferences$ = this._usersService.userPreferences$;
    }

    ngOnInit() {
        this.subscriptions.add(
            this._breakpointObserver.observe('(max-width: 599.98px)').subscribe(state => {
                this.isHandset = state.matches;
                this.updateDisplayedColumns();
            })
        );
        this.updateDisplayedColumns();
        if (null == this.dataSource) {
            throw new Error("proposalsDataSource not set!");
        }
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
        this.summarySubscription?.unsubscribe();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes["bulkActionsEnabled"] || changes["showCustomer"] || changes["variant"]) {
            this.updateDisplayedColumns();
            if (!this.bulkActionsEnabled) {
                this.resetBulkSelection();
            }
        }

        if (changes["forcedFilterValues"] && !changes["forcedFilterValues"].firstChange) {
            this.resetBulkSelection();
            this.proposalsTable?.loadTable();
        }
    }

    /** Cards na Início e no celular; tabela no computador */
    get cardMode(): boolean {
        return this.variant === 'cards' || this.isHandset;
    }

    onTableUpdate(params: any) {
        const mergedParams = this.applyListFilters(this.applyForcedFilters({ ...params }));

        // ORCA-1967: a seleção vale para a página; ao trocar de página ou de filtro, recomeça
        if (this.bulkActionsEnabled && this.pageSelection) {
            this.resetBulkSelection();
        } else if (this.bulkActionsEnabled) {
            const previousSearch = this.tableParams?.filter?.search;
            const currentSearch = mergedParams?.filter?.search;
            if (currentSearch !== undefined && currentSearch !== previousSearch) {
                this.resetBulkSelection();
            }
        }

        this.tableParams = mergedParams;
        this.onFilterChange.emit(mergedParams);
        this.dataSource?.load(mergedParams);
        this.loadSummary();
    }

    onSelectedRow(item: Proposal) {
        if (this.bulkActionsEnabled && this.hasActiveSelection()) {
            return;
        }

        if(this.showDeletedInfo || item.deletedAt){
            this._alertService.error("Não é possível acessar uma proposta excluída. Restaure-a para poder visualizá-la.");
            return;
        }

        this._router.navigate(["propostas", item.token]);
    }

    onSortChange(sort: MatSort) {

        this.sort.ngOnDestroy();
    }

    openAddDialog() {
        this._router.navigate(["propostas", "new"]);
    }

    newProposal(fromEmptyView: boolean = false) {
        if (!this._subscriptionRestrictionService.checkFeatureLimitAndShowDialog("propostas")) {
            if (fromEmptyView) {
                //CRIANDO PROPOSTA A PARTIR DA EMPTYVIEW
                this._gtag.event('empty_view_begin_proposal_create', {
                    event_category: "engagemnt",
                    event_label: "Dispara ao clicar no botão Criar Orçamento na empty view da tabela de orçamentos"
                })
            }
            this._router.navigate(['/propostas/new'])
        }
    }

    // Sempre no formato brasileiro (1.234,56), independente do idioma do navegador
    currencyFormatte(value) {
        return new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(+value || 0)
    }

    // "Excluído" não é status (design system): vai ao lado do status do orçamento.
    // A listagem da API não envia deleted_at; na visão de excluídos todos estão excluídos.
    isDeleted(proposal: Proposal): boolean {
        return this.showDeletedInfo || !!proposal?.deletedAt;
    }

    // Nos excluídos a API manda status 'deleted' e o status real em original_status
    badgeStatus(proposal: Proposal): number | undefined {
        return proposal?.originalStatus ?? proposal?.status;
    }

    /* ORCA-1965: dados da linha ------------------------------------------------------------ */

    situationOf(proposal: Proposal): SituationView {
        return situationView(this.badgeStatus(proposal), proposal?.situation);
    }

    activityOf(proposal: Proposal): string {
        return activityText(proposal?.lastActivity);
    }

    updatedOf(proposal: Proposal): string {
        return updatedText(proposal?.updatedAt);
    }

    createdOf(proposal: Proposal): string {
        return createdText(proposal?.createdAt);
    }

    quoteLabelOf(proposal: Proposal): string {
        return quoteLabel(proposal?.cotacaoId);
    }

    hasMarks(proposal: Proposal): boolean {
        return !!proposal?.cotacaoId || !!proposal?.tags?.length;
    }

    tagStyle(color: string | undefined) {
        return tagStyle(color);
    }

    tagDot(color: string | undefined) {
        return tagDot(color);
    }

    isSent(proposal: Proposal): boolean {
        return Number(proposal?.status) === Proposal.SENT && !this.isDeleted(proposal);
    }

    /** Ação principal dos status que não são "enviado" (o enviado mostra o Aprovar, com contorno verde) */
    mainAction(proposal: Proposal): QuickActionButton | null {
        return this.isDeleted(proposal) || this.isSent(proposal) ? null : quickAction(proposal?.status);
    }

    /** Executa a ação rápida da linha usando o mesmo menu de ações (⋮) do orçamento */
    runQuickAction(event: Event, action: QuickActionButton, proposal: Proposal, actions: ProposalActionsComponent): void {
        event.stopPropagation();

        switch (action.action) {
            case 'aprovar':
            case 'reprovar':
            case 'faturar':
            case 'pagar':
                actions.openActionDialog(action.action);
                break;
            case 'renovar':
                // Renovar validade abre o editor (decisão do ORCA-1962)
                actions.edit();
                break;
            case 'duplicar':
                actions.duplicateProposal();
                break;
            case 'recibo':
                // ORCA-1971: parcelas e recibos num popup, sem abrir o orçamento
                this._matDialog.open(ProposalReceiptsDialogComponent, oaDialogConfig('fullscreen', {
                    data: { proposal },
                    autoFocus: 'dialog',
                }));
                break;
        }
    }

    /**
     * Aprovar, reprovar, faturar ou registrar pagamento: a linha muda na hora com o que a API
     * devolveu; os indicadores e as contagens são recarregados.
     */
    onStatusAction(event: ProposalStatusActionEvent, proposal: Proposal): void {
        Object.assign(proposal, event.patch);
        this.loadSummary();
    }

    /** Depois de "Desfazer" (ou de outra mudança no ⋮) a página é recarregada */
    reload(): void {
        this.proposalsTable?.loadTable();
    }

    /* ORCA-1965: indicadores e chips ---------------------------------------------------------- */

    get monthName(): string {
        return MONTHS[new Date().getMonth()];
    }

    kpiValue(key: Situation): string {
        const indicator = this.summary?.indicators?.[key];
        if (!indicator) {
            return '—';
        }
        return key === 'expiring' ? String(indicator.count) : formatBRL(indicator.amount);
    }

    kpiCount(key: Situation): number {
        return this.summary?.indicators?.[key]?.count ?? 0;
    }

    plural(count: number, one: string, many: string): string {
        return `${count} ${count === 1 ? one : many}`;
    }

    situationCount(key: Situation): string {
        const count = this.summary?.indicators?.[key]?.count;
        return count === undefined ? '' : String(count);
    }

    chipCount(key: StatusChipKey): string {
        const count = this.summary?.status_counts?.[key]?.count;
        return count === undefined ? '' : String(count);
    }

    pickStatus(key: StatusChipKey): void {
        this.statusChip = key;
        this.situation = null;
        this.reloadFromFirstPage();
    }

    pickSituation(situation: Situation | null): void {
        this.situation = situation && this.situation !== situation ? situation : null;
        this.statusChip = 'all';
        this.reloadFromFirstPage();
    }

    trackByProposalId(index: number, proposal: Proposal): number | undefined {
        return proposal.id;
    }

    /*
     * Seleção. Na lista (ORCA-1967) "selecionar todos" marca os orçamentos da página e a barra
     * tem Lembrar clientes, Alterar status, Exportar e Excluir. Nos excluídos (administradores)
     * continua a seleção de todos os filtrados para restaurar.
     */

    get pageSelection(): boolean {
        return this.bulkActionMode === 'delete';
    }

    readonly bulkStatuses = [
        { status: 3, label: 'Aprovado' },
        { status: 4, label: 'Reprovado' },
        { status: 5, label: 'Faturado' },
        { status: 6, label: 'Pago' },
    ];

    selectedProposals(): Proposal[] {
        return (this.dataSource?.data ?? []).filter(p => !!p.id && this.selectedIds.has(p.id));
    }

    selectedSentCount(): number {
        return this.selectedProposals().filter(p => Number(p.status) === Proposal.SENT).length;
    }

    remindSelected(): void {
        const ids = Array.from(this.selectedIds);
        this._proposalService.bulkRemind(ids).subscribe(
            (result) => {
                this.showBulkResult(result, ['lembrete criado nas notificações', 'lembretes criados nas notificações'], ['não era enviado', 'não eram enviados']);
                this._gtag.event('proposal_bulk_remind', { event_category: 'proposal', event_label: 'Lembrar clientes (seleção)', value: result.succeeded });
            },
            (err) => this._alertService.error(err)
        );
    }

    changeSelectedStatus(status: number, label: string): void {
        const total = this.selectedIds.size;
        const dialogRef = this._matDialog.open(ConfirmActionDialogComponent, {
            panelClass: ["form-dialog"],
            data: {
                title: "Alterar status",
                message: `Alterar ${total === 1 ? '1 orçamento' : total + ' orçamentos'} para "${label}"? A data da mudança será hoje.`,
                yesText: "Alterar status",
                noText: "Cancelar",
                confirmVariant: "primary",
            },
        });

        dialogRef.afterClosed().subscribe((confirmed) => {
            if (confirmed !== true) {
                return;
            }

            this._proposalService.bulkStatus(Array.from(this.selectedIds), status).subscribe(
                (result) => {
                    this.showBulkResult(result, ['orçamento alterado', 'orçamentos alterados'], ['não pôde ser alterado', 'não puderam ser alterados']);
                    this._gtag.event('proposal_bulk_status', { event_category: 'proposal', event_label: 'Alterar status (seleção)', proposal_status: status, value: result.succeeded });
                    this.afterBulkChange();
                },
                (err) => this._alertService.error(err)
            );
        });
    }

    exportSelected(): void {
        const params = this._proposalService.paramsFromObject({
            access_token: this._authService.currentUser?.accessToken,
            filter: { ids: Array.from(this.selectedIds).join(',') },
        });

        // a URL leva o access_token: não registrar no console
        window.open(`${this._envService.apiUrl}/v1/clients/proposals/export?${params.toString()}`, '_blank');
    }

    deleteSelected(): void {
        const total = this.selectedIds.size;
        const dialogRef = this._matDialog.open(ConfirmActionDialogComponent, {
            panelClass: ["form-dialog"],
            data: {
                title: "Excluir orçamentos",
                message: `Tem certeza que deseja excluir ${total === 1 ? '1 orçamento' : total + ' orçamentos'}? Eles vão para os excluídos.`,
                yesText: "Excluir",
                noText: "Cancelar",
                confirmVariant: "danger",
            },
        });

        dialogRef.afterClosed().subscribe((confirmed) => {
            if (confirmed !== true) {
                return;
            }

            this._proposalService.bulkDelete(Array.from(this.selectedIds)).subscribe(
                (result) => {
                    this.showBulkResult(result, ['orçamento excluído', 'orçamentos excluídos'], ['não pôde ser excluído', 'não puderam ser excluídos']);
                    this.afterBulkChange();
                    this.bulkActionCompleted.emit("delete");
                },
                (err) => this._alertService.error(err)
            );
        });
    }

    private showBulkResult(result: ProposalBulkResult, done: [string, string], notDone: [string, string]): void {
        let message = bulkResultMessage(result.succeeded, result.failed, done, notDone);
        const firstFailure = result.results.find(r => !r.success && r.message);
        if (firstFailure && result.failed > 0) {
            message += ` (${firstFailure.proposal_number ? '#' + firstFailure.proposal_number + ': ' : ''}${firstFailure.message})`;
        }

        if (result.failed > 0) {
            this._alertService.error(message);
        } else {
            this._alertService.success(message);
        }
    }

    private afterBulkChange(): void {
        this.resetBulkSelection();
        this.proposalsTable?.loadTable();
    }

    isSelected(proposal: Proposal): boolean {
        if (!this.bulkActionsEnabled || !proposal.id) {
            return false;
        }

        if (this.allSelected) {
            return !this.selectedIds.has(proposal.id);
        }

        return this.selectedIds.has(proposal.id);
    }

    toggleSelection(proposal: Proposal): void {
        if (!this.bulkActionsEnabled || !proposal.id) {
            return;
        }

        if (this.pageSelection) {
            if (this.selectedIds.has(proposal.id)) {
                this.selectedIds.delete(proposal.id);
            } else {
                this.selectedIds.add(proposal.id);
            }
            return;
        }

        if (this.allSelected) {
            if (this.selectedIds.has(proposal.id)) {
                this.selectedIds.delete(proposal.id);
            } else {
                this.selectedIds.add(proposal.id);
            }
            return;
        }

        if (this.selectedIds.has(proposal.id)) {
            this.selectedIds.delete(proposal.id);
        } else {
            this.selectedIds.add(proposal.id);
        }

        if (this.selectedIds.size === this.getPageItemCount()) {
            this.masterToggle();
        }
    }

    masterToggle(): void {
        if (!this.bulkActionsEnabled) {
            return;
        }

        if (this.pageSelection) {
            const pageIds = (this.dataSource?.data ?? []).map(p => p.id).filter((id): id is number => !!id);
            const allOnPage = pageIds.length > 0 && pageIds.every(id => this.selectedIds.has(id));
            this.selectedIds.clear();
            if (!allOnPage) {
                pageIds.forEach(id => this.selectedIds.add(id));
            }
            return;
        }

        this.allSelected = !this.allSelected;
        this.selectedIds.clear();
    }

    clearSelection(): void {
        this.resetBulkSelection();
    }

    isAllSelected(): boolean {
        if (this.bulkActionsEnabled && this.pageSelection) {
            const count = this.getPageItemCount();
            return count > 0 && this.selectedIds.size === count;
        }

        return this.bulkActionsEnabled && this.allSelected;
    }

    isSomeSelected(): boolean {
        if (!this.bulkActionsEnabled) {
            return false;
        }

        if (this.pageSelection) {
            return this.selectedIds.size > 0 && this.selectedIds.size < this.getPageItemCount();
        }

        if (this.allSelected) {
            return this.selectedIds.size > 0;
        }

        return this.selectedIds.size > 0 && this.selectedIds.size < this.getPageItemCount();
    }

    hasBulkSelection(): boolean {
        if (!this.bulkActionsEnabled) {
            return false;
        }
        return this.allSelected || this.selectedIds.size > 0;
    }

    getSelectedCount(): number {
        if (!this.bulkActionsEnabled) {
            return 0;
        }

        const totalItems = this.getTotalCount();

        if (this.allSelected) {
            return Math.max(totalItems - this.selectedIds.size, 0);
        }

        return this.selectedIds.size;
    }

    getTotalCount(): number {
        return this.dataSource?.meta?.total ?? 0;
    }

    get bulkActionLabel(): string {
        return this.bulkActionMode === "restore" ? "Restaurar selecionados" : "Excluir selecionados";
    }

    get bulkActionIcon(): string {
        return this.bulkActionMode === "restore" ? "restore_from_trash" : "delete";
    }

    confirmBulkAction(): void {
        if (!this.bulkActionsEnabled || !this.hasBulkSelection()) {
            return;
        }

        const total = this.getSelectedCount();
        const message =
            this.bulkActionMode === "restore"
                ? `Tem certeza que deseja restaurar ${total} orçamento(s)?`
                : `Tem certeza que deseja excluir ${total} orçamento(s)?`;

        const title = this.bulkActionMode === "restore" ? "Restaurar orçamentos" : "Excluir orçamentos";

        const dialogRef = this._matDialog.open(ConfirmActionDialogComponent, {
            panelClass: ["form-dialog"],
            data: {
                title,
                message,
                yesText: this.bulkActionMode === "restore" ? "Restaurar" : "Excluir",
                noText: "Cancelar",
                confirmVariant: this.bulkActionMode === "restore" ? "primary" : "danger",
            },
        });

        dialogRef.afterClosed().subscribe((result) => {
            if (result === true) {
                this.executeBulkAction();
            }
        });
    }

    restoreProposal(event: MouseEvent, proposal: Proposal): void {
        event.stopPropagation();
        if (!proposal.id) {
            return;
        }

        this._proposalService.restore(proposal.id).subscribe(
            () => {
                this._alertService.success("Orçamento restaurado com sucesso!");
                this.resetBulkSelection();
                this.proposalsTable?.loadTable();
                this.bulkActionCompleted.emit("restore");
            },
            (err) => this._alertService.error(err)
        );
    }

    handleProposalDeleted(): void {
        this.bulkActionCompleted.emit("delete");
    }

    private executeBulkAction(): void {
        const ids = Array.from(this.selectedIds);
        const filter = this.tableParams?.filter ?? {};

        const request$ = this.allSelected
            ? this.bulkActionMode === "restore"
                ? this._proposalService.restoreMassiveWithFilters(filter, ids)
                : this._proposalService.destroyMassiveWithFilters(filter, ids)
            : this.bulkActionMode === "restore"
                ? this._proposalService.restoreMany(ids)
                : this._proposalService.destroyMany(ids);

        request$.subscribe(
            () => {
                const successMessage =
                    this.bulkActionMode === "restore"
                        ? "Orçamentos restaurados com sucesso!"
                        : "Orçamentos excluídos com sucesso!";
                this._alertService.success(successMessage);
                this.resetBulkSelection();
                this.proposalsTable?.loadTable();
                this.bulkActionCompleted.emit(this.bulkActionMode);
            },
            (err) => {
                this._alertService.error(err);
                this.proposalsTable?.loadTable();
            }
        );
    }

    private resetBulkSelection(): void {
        this.allSelected = false;
        this.selectedIds.clear();
    }

    private hasActiveSelection(): boolean {
        return this.bulkActionsEnabled && (this.allSelected || this.selectedIds.size > 0);
    }

    private getPageItemCount(): number {
        return this.dataSource?.data?.length ?? 0;
    }

    private reloadFromFirstPage(): void {
        if (this.proposalsTable?.paginator) {
            this.proposalsTable.paginator.pageIndex = 0;
        }
        this.resetBulkSelection();
        this.proposalsTable?.loadTable();
    }

    /** Indicadores e contagens com os mesmos filtros da lista (a API ignora status e indicador) */
    private loadSummary(): void {
        if ((!this.showOverview && !this.compact) || this.showDeletedInfo) {
            return;
        }

        const { page, per_page, sort, ...params } = this.tableParams ?? {};

        this.summarySubscription?.unsubscribe();
        this.summarySubscription = this._proposalService.summary(params).subscribe({
            next: (summary) => this.summary = summary,
            error: () => this.summary = null,
        });
    }

    private updateDisplayedColumns(): void {
        if (this.cardMode) {
            this.displayedColumns = ["card"];
            return;
        }

        const columns = ["image", "description", "status", "valor", "situacao", "acoes"];
        this.displayedColumns = this.bulkActionsEnabled ? ["select", ...columns] : columns;
    }

    /** Chip de status e indicador escolhidos (sobrepõem o status do botão Filtros) */
    private applyListFilters(params: any): any {
        if (!this.situation && this.statusChip === 'all') {
            return params;
        }

        params.filter = { ...(params.filter ?? {}) };

        if (this.situation) {
            params.filter.situation = this.situation;
        }

        const chip = STATUS_CHIPS.find(c => c.key === this.statusChip);
        if (chip?.status) {
            params.filter.filters = { ...(params.filter.filters ?? {}), status: [chip.status] };
        }

        return params;
    }

    private applyForcedFilters(params: any): any {
        if (!this.forcedFilterValues) {
            return params;
        }

        if (!params.filter) {
            params.filter = {};
        }

        if (!params.filter.filters) {
            params.filter.filters = {};
        }

        params.filter.filters = {
            ...params.filter.filters,
            ...this.forcedFilterValues,
        };

        return params;
    }
}
